import {createSlice} from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment';
import API from '../../app/api';
import config from '../../app/config';
import {AlarmGroupDict} from './mock/alarmGroups.mock';
import {setLoading} from './patient';

export const dictsSlice = createSlice({
    name: 'dicts',
    initialState: {
        loading: undefined,
        docTypes: undefined,
        specialties: undefined,
        alarmGroups: undefined,
        urinalysisStripGroups: [],
        gender: [],
        ethnic: [],
        clinicians: [],
        nurses: [],
        beds: [],
        transferHospitals: [],
        clinicalNoteType: [],
        clinicalFrailtyOutcome: [],
        defaultEcdsVersion: 'V3', // Default version of the dictionary used
        currentUsedEcdsVersion: null, // Version of the dictionary used depending on the date
        assessmentList: [],
        barcodes: [],
        trudWards: [],
        streamingType: [],
        dischargeTemplate: [],
        admissionMethods: [],
        attendanceTypes: [],
        attendanceCategories: [],
        conveyingAmbulances: [],
        ecds: {
            accessibleInforProfReq: [],
            accommodationStatus: [],
            acuity: [],
            arrivalMode: [],
            ambulanceCodes: [],
            attendanceSource: [],
            chiefComplaint: [],
            codedAssessment: [],
            codedFinding: [],
            codedObservations: [],
            comorbidity: [],
            diagnosis: [],
            diagnosisQualifier: [],
            dischargeDestination: [],
            dischargeFollowUp: [],
            dischargeInfoGiven: [],
            dischargeStatus: [],
            injuryActivityStatus: [],
            injuryActivityType: [],
            injuryDrugAlcohol: [],
            injuryIntent: [],
            injuryMechanism: [],
            interpreterLanguage: [],
            investigations: [],
            placeOfInjury: [],
            referredToService: [],
            safeguarding: [],
            spokenLanguage: [],
            treatments: [],
            sicknessAbsenceCategory: [],
            referralTreatmentStatus: [],
            waitingTimeMeasurementType: [],
            withheldIdentityReason: [],
            overseasVisitorChargingCategory: [],
            mentalHealthClassification: [],
            departmentType: [],
            attendanceCategory: [],
            activityTreatment: [],
            clinicianIssuerCode: [],
            clinicianTier: [],
            nhsNoVerificationStatuses: [],
            consultationMechanism: [],
            socialAndPersonal: [],
        },
    },
    reducers: {
        setDocTypes: (state, dt) => {
            state.docTypes = dt.payload;
        },
        setSpecialties: (state, specs) => {
            state.specialties = specs.payload;
        },
        setAlarmGroups: (state, alarmGr) => {
            state.alarmGroups = alarmGr.payload;
        },
        setUrinalysisStripGroups: (state, dict) => {
            state.urinalysisStripGroups = dict.payload;
        },
        setGender: (state, dict) => {
            state.gender = dict.payload;
        },
        setEthnic: (state, dict) => {
            state.ethnic = dict.payload;
        },
        setNurses: (state, dict) => {
            state.nurses = dict.payload;
        },
        setBedsDict: (state, dict) => {
            state.beds = dict.payload;
        },
        setClinicians: (state, dict) => {
            state.clinicians = dict.payload;
        },
        setEcdsDict: (state, dict) => {
            state.ecds[dict.payload.name] = dict.payload.dict;
        },
        setLoading: (state, patientId) => {
            state.loading = patientId.payload;
        },
        setTransferHospitals: (state, patientId) => {
            state.transferHospitals = patientId.payload;
        },
        setDefaultEcdsVersion: (state, patientId) => {
            state.defaultEcdsVersion = patientId.payload;
        },
        setCurrentUsedEcdsVersion: (state, patientId) => {
            state.currentUsedEcdsVersion = patientId.payload;
        },
        setClinicalNoteType: (state, patientId) => {
            state.clinicalNoteType = patientId.payload;
        },
        setClinicalFrailtyOutcome: (state, patientId) => {
            state.clinicalFrailtyOutcome = patientId.payload;
        },
        setAssessmentList: (state, payload) => {
            state.assessmentList = payload.payload;
        },
        setBarcodes: (state, payload) => {
            state.barcodes = payload.payload;
        },
        setTrudWards: (state, payload) => {
            state.trudWards = payload.payload;
        },
        setStreamingType: (state, payload) => {
            state.streamingType = payload.payload;
        },
        setDischargeTemplate: (state, payload) => {
            state.dischargeTemplate = payload.payload;
        },
        setAdmissionMethods: (state, payload) => {
            state.admissionMethods = payload.payload;
        },
        setAttendanceTypes: (state, payload) => {
            state.attendanceTypes = payload.payload;
        },
        setAttendanceCategories: (state, payload) => {
            state.attendanceCategories = payload.payload;
        },
        setConveyingAmbulances: (state, payload) => {
            state.conveyingAmbulances = payload.payload;
        },
    },
});

export const {
    setDocTypes,
    setSpecialties,
    setAlarmGroups,
    setEcdsDict,
    setGender,
    setEthnic,
    setNurses,
    setBedsDict,
    setClinicians,
    setUrinalysisStripGroups,
    setTransferHospitals,
    setDefaultEcdsVersion,
    setCurrentUsedEcdsVersion,
    setClinicalNoteType,
    setClinicalFrailtyOutcome,
    setAssessmentList,
    setBarcodes,
    setTrudWards,
    setStreamingType,
    setDischargeTemplate,
    setAdmissionMethods,
    setAttendanceTypes,
    setAttendanceCategories,
    setConveyingAmbulances
} = dictsSlice.actions;

export const docTypes = state => state.dicts.docTypes;
export const specialties = state => state.dicts.specialties;
export const alarmGroups = state => state.dicts.alarmGroups;
export const urinalysisStripGroups = state => state.dicts.urinalysisStripGroups;
export const gendersDict = state => state.dicts.gender;
export const ethnicGroupsDict = state => state.dicts.ethnic;
export const nursesDict = state => state.dicts.nurses;
export const cliniciansDict = state => state.dicts.clinicians;
export const transferHospitalsDict = state => state.dicts.transferHospitals;
export const defaultEcdsVersion = state => state.dicts.defaultEcdsVersion;
export const currentUsedEcdsVersion = state => state.dicts.currentUsedEcdsVersion;
export const clinicalNoteTypeDict = state => state.dicts.clinicalNoteType;
export const clinicalFrailtyOutcomeDict = state => state.dicts.clinicalFrailtyOutcome;
export const agyleAssessmentList = state => state.dicts.assessmentList;
export const oldBedsDict = state => state.dicts.beds;
export const barcodesDict = state => state.dicts.barcodes;
export const trudWardsDict = state => state.dicts.trudWards;
export const streamingTypeDict = state => state.dicts.streamingType;
export const dischargeTemplateDict = state => state.dicts.dischargeTemplate;
export const admissionMethods = state => state.dicts.admissionMethods;
export const attendanceTypes = state => state.dicts.attendanceTypes;
export const attendanceCategories = state => state.dicts.attendanceCategories;
export const conveyingAmbulances = state => state.dicts.conveyingAmbulances;

// ECDS Dictionaries
export const accessibleInforProfReq = state => state.dicts.ecds.accessibleInforProfReq;
export const accommodationStatus = state => state.dicts.ecds.accommodationStatus;
export const acuity = state => state.dicts.ecds.acuity;
export const arrivalModes = state => state.dicts.ecds.arrivalMode;
export const ambulanceCodes = state => state.dicts.ecds.ambulanceCodes;
export const attendanceSource = state => state.dicts.ecds.attendanceSource;
export const chiefComplaint = state => state.dicts.ecds.chiefComplaint;
export const codedAssessment = state => state.dicts.ecds.codedAssessment;
export const codedFinding = state => state.dicts.ecds.codedFinding;
export const codedObservations = state => state.dicts.ecds.codedObservations;
export const comorbiditis = state => state.dicts.ecds.comorbidity;
export const diagnosis = state => state.dicts.ecds.diagnosis;
export const diagnosisQualifier = state => state.dicts.ecds.diagnosisQualifier;
export const dischargeDestination = state => state.dicts.ecds.dischargeDestination;
export const dischargeFollowUp = state => state.dicts.ecds.dischargeFollowUp;
export const dischargeInfoGiven = state => state.dicts.ecds.dischargeInfoGiven;
export const dischargeStatus = state => state.dicts.ecds.dischargeStatus;
export const injuryActivityStatus = state => state.dicts.ecds.injuryActivityStatus;
export const injuryActivityType = state => state.dicts.ecds.injuryActivityType;
export const injuryDrugAlcohol = state => state.dicts.ecds.injuryDrugAlcohol;
export const injuryIntent = state => state.dicts.ecds.injuryIntent;
export const injuryMechanism = state => state.dicts.ecds.injuryMechanism;
export const interpreterLanguage = state => state.dicts.ecds.interpreterLanguage;
export const investigations = state => state.dicts.ecds.investigations;
export const placeOfInjury = state => state.dicts.ecds.placeOfInjury;
export const referredToService = state => state.dicts.ecds.referredToService;
export const safeguarding = state => state.dicts.ecds.safeguarding;
export const spokenLanguage = state => state.dicts.ecds.spokenLanguage;
export const treatments = state => state.dicts.ecds.treatments;
export const sicknessAbsenceCategory = state => state.dicts.ecds.sicknessAbsenceCategory;
export const socialAndPersonalDict = state => state.dicts.ecds.socialAndPersonal;
// Other ECDS Related Dictionaries
export const referralTreatmentStatus = state => state.dicts.ecds.referralTreatmentStatus;
export const waitingTimeMeasurementType = state => state.dicts.ecds.waitingTimeMeasurementType;
export const withheldIdentityReason = state => state.dicts.ecds.withheldIdentityReason;
export const overseasVisitorChargingCategory = state => state.dicts.ecds.overseasVisitorChargingCategory;
export const mentalHealthClassification = state => state.dicts.ecds.mentalHealthClassification;
export const departmentType = state => state.dicts.ecds.departmentType;
export const attendanceCategory = state => state.dicts.ecds.attendanceCategory;
export const activityTreatment = state => state.dicts.ecds.activityTreatment;
export const clinicianIssuerCode = state => state.dicts.ecds.clinicianIssuerCode;
export const clinicianTier = state => state.dicts.ecds.clinicianTier;
export const nhsNoVerificationStatuses = state => state.dicts.ecds.nhsNoVerificationStatuses;
export const consultationMechanism = state => state.dicts.ecds.consultationMechanism;

export const loadDicts = (dispatch, getState) => {
    if (getState().dicts.loading !== true) {
        dispatch(setLoading(true));
        if (process.env.BUILD_MODULE === 'lwh' || process.env.BUILD_MODULE === 'stg' || process.env.BUILD_MODULE === 'dch') {
            API.getDictByURL('/$dict/DocumentSpecialties').then(specialties => {
                specialties = _.sortBy(specialties.__data, o => o.__description);
                dispatch(setSpecialties(specialties));
                API.getDictByURL('/DocTypes?$expand=tab').then(dts => {
                    dts = _.sortBy(dts.__data, o => o.name);
                    dispatch(setDocTypes(dts));
                    dispatch(setLoading(undefined));
                });
            }).catch(function (error) {
                console.error(error);
            });
        } else if (process.env.BUILD_MODULE === 'agyle') {
            dispatch(loadClinicians);
            dispatch(loadECDSDicts);
            if (_.isEmpty(getState().dicts.alarmGroups)) {
                API.getDictByURL('/$dict/alertGroups')
                    .then(alarmGroups => {
                        if (alarmGroups) {
                            alarmGroups = _.sortBy(alarmGroups.__data, o => o.name);
                            dispatch(setAlarmGroups(alarmGroups));
                        } else {
                            dispatch(setAlarmGroups(AlarmGroupDict));
                        }
                        dispatch(setLoading(undefined));
                    }).catch(function (error) {
                    console.error(error);
                });
            }
            if (_.isEmpty(getState().dicts.ethnic)) {
                API.getDictByURL('/$dict/EthnicGroups?$sort=description')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setEthnic(_.sortBy(dict.__data, ['__description'])));
                        }
                    })
                    .catch((error) => {
                        console.error('EthnicGroups error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.gender)) {
                API.getDictByURL('/$dict/Genders?$sort=description')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setGender(_.sortBy(dict.__data, ['__description'])));
                        }
                    })
                    .catch((error) => {
                        console.error('Genders error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.urinalysisStripGroups)) {
                API.getDictByURL('/$dict/UrinalysisStrips')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setUrinalysisStripGroups(_.sortBy(dict.__data, ['__description'])));
                        }
                    })
                    .catch((error) => {
                        console.error('UrinalysisStrips error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.beds)) {
                API.getDictByURL('/$dict/Bed')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setBedsDict(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Bed Dict error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.transferHospitals)) {
                API.getDictByURL('/$dict/TransferHospitals')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setTransferHospitals(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Transfer Hospitals error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.clinicalNoteType)) {
                API.getDictByURL('/$dict/ClinicalNoteType')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setClinicalNoteType(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Clinical Note Type error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.clinicalFrailtyOutcome)) {
                API.getDictByURL('/$dict/ClinicalFrailtyOutcome')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setClinicalFrailtyOutcome(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Clinical Note Type error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.assessmentList)) {
                API.getDictByURL('/agyle-assessment')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setAssessmentList(_.sortBy(dict.__data, ['description'])));
                        }
                    })
                    .catch((error) => {
                        console.error('Agyle Assessment List error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.barcodes)) {
                API.getDictByURL('/$dict/Barcode')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setBarcodes(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Barcode error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.streamingType)) {
                API.getDictByURL('/$dict/StreamingType')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setStreamingType(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Streaming Type error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.dischargeTemplate)) {
                const version = dispatch(getEcdsVersion(moment()));
                API.getDischargeTemplates(version)
                    .then(templateRes => {
                        if (templateRes && templateRes.__data) {
                            dispatch(setDischargeTemplate(templateRes.__data?.filter(d => !d?.deactivated)));
                        }
                    })
                    .catch((error) => {
                        console.error('Discharge Templates ver and error', version, error);
                    });
            }
            if (_.isEmpty(getState().dicts.admissionMethods)) {
                API.getDictByURL('/$dict/AdmissionMethods')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setAdmissionMethods(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Admission Methods error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.attendanceTypes)) {
                API.getDictByURL('/$dict/AttendanceTypes')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setAttendanceTypes(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Attendance Types error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.attendanceCategories)) {
                API.getDictByURL('/$dict/AttendanceCategory')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setAttendanceCategories(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Attendance Categories error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.conveyingAmbulances)) {
                API.getDictByURL('/$dict/ConveyingAmbulances')
                    .then(dict => {
                        if (dict && dict.__data) {
                            dispatch(setConveyingAmbulances(dict.__data));
                        }
                    })
                    .catch((error) => {
                        console.error('Conveying Ambulances error', error);
                    });
            }
            if (_.isEmpty(getState().dicts.trudWards)) {
                API.getAllWards()
                    .then(dict => {
                        if (dict?.data && _.isArray(dict?.data)) {
                            const wardsTrud = [...dict.data];
                            // If the ward list doesn't have an ED, then insert a fake one.
                            if (_.findIndex(wardsTrud, {wardCode: 'ED'}) === -1) {
                                wardsTrud.push({
                                    wardCode: 'ED',
                                    wardName: 'EMERGENCY DEPARTMENT',
                                    hospital: null,
                                    __id: 'ED',
                                });
                            }
                            dispatch(setTrudWards(_.sortBy(wardsTrud, ['wardName'])));
                        }
                    })
                    .catch((error) => {
                        console.error('Trud Ward List error', error);
                    });
            }
            dispatch(setLoading(undefined));
        } else {
            API.getDictByURL('/DocTypes').then(dts => {
                dts = _.sortBy(dts.__data, o => o.name);
                dispatch(setDocTypes(dts));
                dispatch(setLoading(undefined));
            }).catch(function (error) {
                console.error(error);
            });
        }
    }
};

export const loadClinicians = (dispatch, getState) => {
    if (_.isEmpty(getState().dicts.clinicians)) {
        API.getDictByURL('/$dict/Clinicians')
            .then(dict => {
                if (dict && dict.__data) {
                    dispatch(setClinicians(dict.__data));
                }
            })
            .catch((error) => {
                console.error('setClinicians error', error);
            });
    }

    if (_.isEmpty(getState().dicts.nurses)) {
        API.getDictByURL('/$dict/Nurses')
            .then(dict => {
                if (dict && dict.__data) {
                    dispatch(setNurses(dict.__data));
                }
            })
            .catch((error) => {
                console.error('setNurses error', error);
            });
    }
};

export const getDictForECDS = (dictName) => (dispatch, getState) => {
    switch (dictName) {
        case 'mentalHealthClassification':
            return getState().dicts.ecds.mentalHealthClassification;
        case 'scoredAssessment':
            return getState().dicts.ecds.codedAssessment;
        case 'clinicalObservations':
            return getState().dicts.ecds.codedObservations;
        case 'clinicalFindings':
            return getState().dicts.ecds.codedFinding;
        case 'injuryAlcoholOrDrug':
            return getState().dicts.ecds.injuryDrugAlcohol;
        case 'comorbidity':
            return getState().dicts.ecds.comorbidity;
        case 'clinicianIssuerCode':
            return getState().dicts.ecds.clinicianIssuerCode;
        case 'clinicianTier':
            return getState().dicts.ecds.clinicianTier;
        case 'diagnosis':
            return getState().dicts.ecds.diagnosis;
        case 'diagnosisQualifier':
            return getState().dicts.ecds.diagnosisQualifier;
        case 'investigation':
            return getState().dicts.ecds.investigations;
        case 'treatment':
            return getState().dicts.ecds.treatments;
        case 'referrals':
            return getState().dicts.ecds.referredToService;
        case 'safeguarding':
            return getState().dicts.ecds.safeguarding;
        case 'treatmentStatus':
            return getState().dicts.ecds.referralTreatmentStatus;
        case 'waitingTimeMeasurementType':
            return getState().dicts.ecds.waitingTimeMeasurementType;
        case 'nhsCodeStatus':
            return getState().dicts.ecds.nhsNoVerificationStatuses;
        case 'withheldReason':
            return getState().dicts.ecds.withheldIdentityReason;
        case 'gender':
            return getState().dicts.gender;
        case 'ethnic':
            return getState().dicts.ethnic;
        case 'spokenLang':
            return getState().dicts.ecds.spokenLanguage;
        case 'interStatus':
            return getState().dicts.ecds.accessibleInforProfReq;
        case 'interLang':
            return getState().dicts.ecds.interpreterLanguage;
        case 'accommodation':
            return getState().dicts.ecds.accommodationStatus;
        case 'overseasVisitorCat':
            return getState().dicts.ecds.overseasVisitorChargingCategory;
        case 'departmentType':
            return getState().dicts.ecds.departmentType;
        case 'attendanceCat':
            return getState().dicts.ecds.attendanceCategory;
        case 'arrival':
            return getState().dicts.ecds.arrivalMode;
        case 'attSource':
            return getState().dicts.ecds.attendanceSource;
        case 'acuity':
            return getState().dicts.ecds.acuity;
        case 'chiefComplain':
            return getState().dicts.ecds.chiefComplaint;
        case 'placeOfInjury':
            return getState().dicts.ecds.placeOfInjury;
        case 'injuryIntent':
            return getState().dicts.ecds.injuryIntent;
        case 'injuryActivityStatus':
            return getState().dicts.ecds.injuryActivityStatus;
        case 'injuryActivityType':
            return getState().dicts.ecds.injuryActivityType;
        case 'injuryMechanism':
            return getState().dicts.ecds.injuryMechanism;
        case 'activityTreatment':
            return getState().dicts.ecds.activityTreatment;
        case 'dischargeStatus':
            return getState().dicts.ecds.dischargeStatus;
        case 'dischargeDestination':
            return getState().dicts.ecds.dischargeDestination;
        case 'dischargeFollowUp':
            return getState().dicts.ecds.dischargeFollowUp;
        case 'dischargeInfoGiven':
            return getState().dicts.ecds.dischargeFollowUp;
        case 'sicknessAbsenceCategory':
            return getState().dicts.ecds.sicknessAbsenceCategory;
        case 'consultationMechanism':
            return getState().dicts.ecds.consultationMechanism;
        case 'socialAndPersonal':
            return getState().dicts.ecds.socialAndPersonal;

        default:
            return [];
    }
};

const dictRequest = (address, dict, sort, filter, ecds) => {
    return {address, dict, sort, filter, ecds};
};

const loadECDSDicts = (dispatch, getState) => {
    const ecds = [
        dictRequest('EcdsSpokenLanguage', 'spokenLanguage', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsAccessibleInforProfReq', 'accessibleInforProfReq', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInterpreterLanguage', 'interpreterLanguage', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsAccommodationStatus', 'accommodationStatus', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsAcuity', 'acuity', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsArrivalMode', 'arrivalMode', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsAttendanceSource', 'attendanceSource', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsChiefOfComplaint', 'chiefComplaint', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsCodedAssessment', 'codedAssessment', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsCodedObservations', 'codedObservations', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsCodedFinding', 'codedFinding', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsPlaceOfInjury', 'placeOfInjury', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInjuryIntent', 'injuryIntent', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInjuryActivityStatus', 'injuryActivityStatus', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInjuryActivityType', 'injuryActivityType', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInjuryMechanism', 'injuryMechanism', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInjuryDrugAlcohol', 'injuryDrugAlcohol', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsComorbidity', 'comorbidity', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDischargeStatus', 'dischargeStatus', '!group,sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsSafeguarding', 'safeguarding', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDischargeDestination', 'dischargeDestination', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDischargeFollowUp', 'dischargeFollowUp', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDischargeInfoGiven', 'dischargeInfoGiven', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDiagnosisQualifier', 'diagnosisQualifier', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsDiagnosis', 'diagnosis', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsTreatments', 'treatments', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsInvestigations', 'investigations', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsReferredToService', 'referredToService', 'sort1,sort2,sort3,sort4', 'active eq 1 or active eq true', true),
        dictRequest('EcdsSicknessAbsenceCategory', 'sicknessAbsenceCategory', 'sort1,sort2,sort3,sort4', null, true),
        dictRequest('EcdsSocialAndPersonals', 'socialAndPersonal', 'group,description', null, true),
        dictRequest('ReferralTreatmentStatus', 'referralTreatmentStatus', 'description'),
        dictRequest('WaitingTimeMeasurementType', 'waitingTimeMeasurementType', 'description'),
        dictRequest('WithheldIdentityReason', 'withheldIdentityReason', 'description'),
        dictRequest('OverseasVisitorChargingCategory', 'overseasVisitorChargingCategory', 'description'),
        dictRequest('MentalHealthClassification', 'mentalHealthClassification', 'description'),
        dictRequest('DepartmentType', 'departmentType', 'description'),
        dictRequest('AttendanceCategory', 'attendanceCategory', 'code'),
        dictRequest('ActivityTreatment', 'activityTreatment', 'description'),
        dictRequest('ClinicianIssuerCode', 'clinicianIssuerCode', 'description'),
        dictRequest('ClinicianTier', 'clinicianTier', 'description'),
        dictRequest('NhsNoVerificationStatuses', 'nhsNoVerificationStatuses', 'description'),
        dictRequest('ConsultationMechanism', 'consultationMechanism', 'description'),
    ];

    const ecdsVersion = dispatch(getEcdsVersion(moment()));
    dispatch(setDefaultEcdsVersion(ecdsVersion));

    _.forEach(ecds, e => {
        if (_.isEmpty(getState().dicts.ecds[e.dict])) {
            let filter = '';
            if (e?.ecds || e?.filter) {
                let filterToURL = '';
                if (e?.ecds) {
                    filterToURL = encodeURIComponent(`deactivated ne true`);
                }
                if (e?.filter) {
                    filterToURL += `${!_.isEmpty(filterToURL) ? ' and ' : ' '}${encodeURIComponent(e?.filter)}`;
                }
                filter = `$filter=${filterToURL}&`;
            }
            API.getDictByURL(`/$dict/${e.address}?${filter}$sort=${e?.sort}`)
                .then(dict => {
                    if (dict?.__data) {
                        dispatch(setEcdsDict({
                            name: e.dict,
                            dict: dict.__data,
                        }));

                        if (e.dict === 'arrivalMode') {
                            const ambulance = [];
                            _.forEach(dict?.__data, dict => {
                                if (dict?.group === 'ambulance' || dict?.ambulance) ambulance.push(dict.__code);
                            });
                            dispatch(setEcdsDict({
                                name: 'ambulanceCodes',
                                dict: ambulance,
                            }));
                        }
                    }
                })
                .catch((error) => {
                    console.error(`${e.address} error`, error);
                });
        }
    });
};

export const getEcdsVersion = (checkDate) => (dispatch, getState) => {
    let currentVersion = getState().dicts.defaultEcdsVersion;
    if (config?.modules?.forms?.ecds?.activationDateOfVersion4 && checkDate) {
        const enableV4 = moment(checkDate).isAfter(moment(config?.modules?.forms?.ecds?.activationDateOfVersion4));
        if (enableV4) currentVersion = 'V4';
        else currentVersion = 'V3';
    }
    return currentVersion;
};

export const reloadAssessmentList = () => (dispatch) => {
    API.getDictByURL('/agyle-assessment')
        .then(dict => {
            if (dict && dict.__data) {
                dispatch(setAssessmentList(_.sortBy(dict.__data, ['description'])));
            }
        })
        .catch((error) => {
            console.error('Agyle Assessment List error', error);
        });
};

export default dictsSlice.reducer;
